import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import * as PackageInventoryStore from '../../../../redux/packageInventory';
import useSortedVulnerabilities from '../../hooks/useSortedVulnerabilities';
import type { SortableColumn, SortType } from '../types';

import VulnerabilityDrawer from './Drawer';
import VulnerabilitiesTable from './Table';

const DRAWER_ANIMATION_TIMEOUT = 500;

type VulnerabilitiesTableContainerProps = {
  params: {
    packageLocator: string;
    version: string;
  };
};

const VulnerabilitiesTableContainer: VoidFunctionComponent<VulnerabilitiesTableContainerProps> = ({ params }) => {
  const dispatch = useDispatch();

  //  Note that we can't gate this with a isFeatureEnabled check as it violates the Rules of Hooks™️
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [sortByColumn, setSortByColumn] = useState<SortableColumn>('cvss');
  const [sortType, setSortType] = useState<SortType>('desc');
  const sortedVulnerabilities = useSortedVulnerabilities(sortByColumn, sortType);

  //  This is used to ignore clicks on the table body when the Drawer is open, so that it doesn't close when we click "outside" the Drawer onto the table
  const tableBodyRef = useRef<HTMLTableSectionElement>(null);

  const locator = `${params.packageLocator}$${params.version}`;
  const onOpenDrawer = (vulnId: string) => {
    if (!isDrawerOpen) {
      setIsDrawerOpen(true);
    }
    //  This needs to be outside the isDrawerOpen so that users can click OTHER vulns.
    //  Note that fetchSelectedVuln will bail if attempting to select the same vuln.
    dispatch(PackageInventoryStore.actions.fetchSelectedVuln(vulnId, locator));
  };

  const onCloseDrawer = () => {
    setIsDrawerOpen(false);
    //  Wait until the "close" animation is done to
    setTimeout(() => {
      dispatch(PackageInventoryStore.actions.resetSelectedVuln());
      //  Add 10ms padding before clearing the selected vuln, allowing the animation to fully complete.
    }, DRAWER_ANIMATION_TIMEOUT + 10);
  };

  return (
    <div className="container padding-top-lg" data-test-id="package-inventory-package-version">
      <VulnerabilityDrawer
        isOpen={isDrawerOpen}
        onClose={onCloseDrawer}
        version={params.version}
        packageRevisionLocator={locator}
        tableBodyRef={tableBodyRef}
        slideDuration={DRAWER_ANIMATION_TIMEOUT}
      />
      <VulnerabilitiesTable
        sortByColumn={sortByColumn}
        setSortByColumn={setSortByColumn}
        sortType={sortType}
        setSortType={setSortType}
        vulnerabilities={sortedVulnerabilities}
        onVulnSelect={onOpenDrawer}
        tableBodyRef={tableBodyRef}
      />
    </div>
  );
};

export default VulnerabilitiesTableContainer;
